import { scriptCounts, type ScriptCounts } from "./detectLanguage";
import { isJaSource, otherLanguageOf, type Direction, type OtherLanguage } from "./translateLanguages";

// Sanity checks for the MyMemory fallback (see translate.ts). MyMemory is a
// crowd-sourced translation memory with no quality gate: it can answer with text
// in the wrong language (a poisoned entry tagged fr-FR that is really Arabic), or
// with an echo of the input. Google's results are never run through this.
//
// The check is by WRITING SYSTEM only. Latin script cannot tell English from
// Portuguese, Spanish, French, German, Dutch or Italian, and this file does not
// try: a Spanish answer to a French request, or MyMemory's raw machine output
// without capitals or punctuation, passes. All it catches is output that cannot
// be in the target language at all (Arabic for French, kana for Chinese, ...).

type Script = keyof ScriptCounts;
type TargetLanguage = "ja" | OtherLanguage;

// Which script(s) must make up the letters of a translation INTO each language.
// A Record over every language, so adding one to OTHER_LANGUAGES fails to
// compile until it is listed here.
const EXPECTED_SCRIPTS: Record<TargetLanguage, readonly Script[]> = {
    ja: ["kana", "han"],
    zh: ["han"],
    ko: ["hangul"],
    ru: ["cyrillic"],
    ar: ["arabic"],
    en: ["latin"],
    pt: ["latin"],
    es: ["latin"],
    fr: ["latin"],
    de: ["latin"],
    nl: ["latin"],
    it: ["latin"],
};

// At least this share of the letters must be in the target's script. Half also
// means a Latin-script translation can carry up to half of its letters as
// Japanese/Chinese proper nouns or quotations and still be accepted.
const MIN_EXPECTED_SHARE = 0.5;

// Chinese is written in Han, but so is most Japanese; a "Chinese" answer with
// this much kana in it is Japanese.
const MAX_KANA_SHARE_IN_CHINESE = 0.2;

// MyMemory scores each stored translation 0-1; below this it is a loose fuzzy
// match, not the sentence that was asked for.
const MIN_USABLE_MATCH = 0.7;

const SCRIPT_KEYS: readonly Script[] = ["kana", "hangul", "cyrillic", "arabic", "han", "latin"];

function totalLetters(counts: ScriptCounts): number {
    return SCRIPT_KEYS.reduce((sum, script) => sum + counts[script], 0);
}

function targetLanguageOf(direction: Direction): TargetLanguage {
    // Every Direction is "ja-<code>" or "<code>-ja" (see translateLanguages.ts).
    return isJaSource(direction) ? otherLanguageOf(direction) : "ja";
}

function hasTargetScript(counts: ScriptCounts, target: TargetLanguage, letters: number): boolean {
    const expected = EXPECTED_SCRIPTS[target].reduce((sum, script) => sum + counts[script], 0);
    if (expected / letters < MIN_EXPECTED_SHARE) return false;
    return target !== "zh" || counts.kana / letters < MAX_KANA_SHARE_IN_CHINESE;
}

// Letters and digits only, case- and width-folded, so "Hello." and " hello " compare equal.
function comparable(text: string): string {
    return text.normalize("NFKC").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
}

// Han-only text is shared by Japanese and Chinese (学生, 大学, ...), so for those
// two targets an output identical to a Han-only input can be the correct answer
// rather than an untranslated echo.
function mayLegitimatelyMatchInput(target: TargetLanguage, input: string): boolean {
    if (target !== "ja" && target !== "zh") return false;
    const counts = scriptCounts(input);
    return counts.han > 0 && counts.han === totalLetters(counts);
}

// True when `output` could be a real translation of `input` into the language
// `direction` points at.
//  - blank output: no (nothing to show)
//  - no letters at all (numbers, punctuation): yes
//  - identical to the input once punctuation/case are ignored: no, MyMemory
//    handed the text back untranslated
//  - fewer than half of the letters in the target's script: no
//  - target Chinese with 20% or more kana: no, that is Japanese
export function isPlausibleTranslation(output: string, direction: Direction, input: string): boolean {
    if (!output.trim()) return false;
    const counts = scriptCounts(output);
    const letters = totalLetters(counts);
    if (letters === 0) return true;

    const target = targetLanguageOf(direction);
    const isEcho = comparable(output) === comparable(input);
    if (isEcho && !mayLegitimatelyMatchInput(target, input)) return false;
    return hasTargetScript(counts, target, letters);
}

export type MyMemoryPick =
    // `text` passed isPlausibleTranslation.
    | { status: "ok"; text: string }
    // MyMemory offered no text at all.
    | { status: "empty" }
    // It returned text, but none of it is plausible.
    | { status: "rejected" };

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

// The translations MyMemory offers for one request, best first: its own headline
// answer (responseData.translatedText), then the stored matches in the order it
// listed them, skipping loose fuzzy matches. Each match carries a source/target
// language tag, but those tags cannot be trusted (the Arabic entry for French was
// tagged fr-FR), so only the text is judged.
function candidatesOf(data: unknown): string[] {
    if (!isRecord(data)) return [];
    const candidates: string[] = [];
    const headline = isRecord(data.responseData) ? data.responseData.translatedText : undefined;
    if (typeof headline === "string") candidates.push(headline);
    if (Array.isArray(data.matches)) {
        data.matches.forEach((entry: unknown) => {
            if (isRecord(entry) && typeof entry.translation === "string" && Number(entry.match) >= MIN_USABLE_MATCH) {
                candidates.push(entry.translation);
            }
        });
    }
    return candidates.filter((candidate) => candidate.trim() !== "");
}

// Picks the first plausible translation out of a parsed MyMemory response. `input`
// is the text that was sent (for a long text, the one chunk this response is for).
export function pickMyMemoryTranslation(data: unknown, direction: Direction, input: string): MyMemoryPick {
    const candidates = candidatesOf(data);
    if (candidates.length === 0) return { status: "empty" };
    const plausible = candidates.find((candidate) => isPlausibleTranslation(candidate, direction, input));
    return plausible === undefined ? { status: "rejected" } : { status: "ok", text: plausible };
}
